import {
  ActionIcon,
  Badge,
  Button,
  Flex,
  Group,
  Modal,
  Select,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { EpiTutorial } from '../../components/epi-tutorial';
import { useForm } from '@mantine/form';
import { IconTrash, IconX } from '@tabler/icons';
import { DataTable } from 'mantine-datatable';
import { useEffect, useRef, useState } from 'react';

import { CustomDatePicker } from '../../components/custom-date-picker';
import { Page } from '../../components/page';
import {
  useDeleteEpiDelivery,
  useEpiCatalog,
  useListEpiDeliveries,
} from '../../data/hooks/employee';
import { useGetUser } from '../../data/hooks/user';
import {
  EpiDelivery,
  EpiDeliveryReasonLabel,
  EpiDeliveryReasonOptions,
} from '../../models/employees';
import { formatLocale } from '../../providers/dayjs-plugins';

export function EpiDeliveryList() {
  const page = useRef(1);
  const pageLimit = useRef(15);
  const [deleteTarget, setDeleteTarget] = useState<EpiDelivery | null>(null);
  const [deleteOpened, { open: openDelete, close: closeDelete }] =
    useDisclosure(false);

  const { fetch: listUsersFetcher, response: users } = useGetUser();
  const { list: listCatalogFetcher, catalog } = useEpiCatalog();
  const {
    fetch: listDeliveriesFetcher,
    response: deliveries,
    loading: listLoading,
  } = useListEpiDeliveries();
  const { remove: deleteDelivery, loading: deleteLoading } =
    useDeleteEpiDelivery();

  const formFilter = useForm({
    initialValues: {
      employeeId: '',
      epiId: '',
      reason: '',
      dateBegin: null as Date | null,
      dateEnd: null as Date | null,
    },
  });

  useEffect(() => {
    getDeliveries();
    listCatalogFetcher();
    listUsersFetcher();
  }, []);

  function getDeliveries(resetPage = false) {
    if (resetPage) page.current = 1;
    listDeliveriesFetcher({
      employeeId: formFilter.values.employeeId
        ? Number(formFilter.values.employeeId)
        : undefined,
      epiId: formFilter.values.epiId
        ? Number(formFilter.values.epiId)
        : undefined,
      startDate: formFilter.values.dateBegin
        ? formFilter.values.dateBegin.toISOString()
        : undefined,
      endDate: formFilter.values.dateEnd
        ? new Date(
            formFilter.values.dateEnd.setHours(23, 59, 59, 999),
          ).toISOString()
        : undefined,
      page: page.current,
      limit: pageLimit.current,
    });
  }

  function handleClearFilter() {
    formFilter.reset();
    page.current = 1;
    listDeliveriesFetcher({ page: 1, limit: pageLimit.current });
  }

  function handleDeleteClick(record: EpiDelivery) {
    setDeleteTarget(record);
    openDelete();
  }

  function handleConfirmDelete() {
    if (!deleteTarget?.id) return;
    deleteDelivery(deleteTarget.id, () => {
      closeDelete();
      setDeleteTarget(null);
      getDeliveries();
    });
  }

  const filterUserOptions =
    users
      ?.filter((u) => u.employee?.id || u.employeeId)
      .map((u) => ({
        label: u.username,
        value: String(u.employee?.id || u.employeeId),
      })) || [];

  const filterEpiOptions =
    catalog?.map((e) => ({ label: e.name, value: String(e.id) })) || [];

  return (
    <Page>
      <Modal
        opened={deleteOpened}
        onClose={closeDelete}
        title="Excluir registro de entrega"
        centered
      >
        <Text size="sm" mb="md">
          Tem certeza que deseja excluir este registro de entrega?
          {deleteTarget && (
            <>
              {' '}
              <strong>
                {deleteTarget.employee?.user?.username} —{' '}
                {deleteTarget.epi?.name}
              </strong>{' '}
              ({formatLocale(deleteTarget.createdAt, 'DD/MM/YYYY HH:mm')})
            </>
          )}
        </Text>
        <Text size="xs" color="dimmed" mb="lg">
          Esta ação não pode ser desfeita.
        </Text>
        <Group position="right">
          <Button variant="default" onClick={closeDelete}>
            Cancelar
          </Button>
          <Button
            color="red"
            loading={deleteLoading}
            onClick={handleConfirmDelete}
          >
            Excluir
          </Button>
        </Group>
      </Modal>

      <Flex direction="column" gap="md">
        <Group position="apart" align="flex-start">
          <div>
            <Title size={24}>Histórico de Entregas de EPIs</Title>
            <Text color="dimmed" size="sm">
              Todos os registros de equipamentos entregues aos funcionários.
            </Text>
          </div>
          <EpiTutorial screen="deliveries" />
        </Group>

        <form onSubmit={formFilter.onSubmit(() => getDeliveries(true))}>
          <Flex align="flex-end" wrap="wrap">
            <Select
              label="Funcionário"
              placeholder="todos"
              clearable
              searchable
              data={filterUserOptions}
              mb={16}
              mr={8}
              sx={{ width: 200 }}
              {...formFilter.getInputProps('employeeId')}
            />
            <Select
              label="EPI"
              placeholder="todos"
              clearable
              searchable
              data={filterEpiOptions}
              mb={16}
              mr={8}
              sx={{ width: 200 }}
              {...formFilter.getInputProps('epiId')}
            />
            <Select
              label="Motivo"
              placeholder="todos"
              clearable
              data={EpiDeliveryReasonOptions}
              mb={16}
              mr={8}
              sx={{ width: 200 }}
              {...formFilter.getInputProps('reason')}
            />
            <CustomDatePicker
              label="Data Inicial"
              placeholder="selecione uma data"
              maw={170}
              mb={16}
              mr={8}
              {...formFilter.getInputProps('dateBegin')}
            />
            <CustomDatePicker
              label="Data Final"
              placeholder="selecione uma data"
              maw={170}
              mb={16}
              mr={8}
              {...formFilter.getInputProps('dateEnd')}
            />
            <Button mb={16} type="submit">
              Filtrar
            </Button>
            <Button
              mb={16}
              ml={8}
              variant="outline"
              leftIcon={<IconX size={14} />}
              onClick={handleClearFilter}
            >
              Limpar
            </Button>
          </Flex>
        </form>

        <DataTable
          withBorder
          borderRadius="sm"
          striped
          highlightOnHover
          records={deliveries?.items || []}
          fetching={listLoading}
          noRecordsText="Nenhum registro de entrega encontrado"
          height="70vh"
          totalRecords={deliveries?.meta?.totalItems}
          recordsPerPage={pageLimit.current}
          page={page.current}
          onPageChange={(p) => {
            page.current = p;
            getDeliveries();
          }}
          recordsPerPageOptions={[15, 50, 100]}
          onRecordsPerPageChange={(l) => {
            pageLimit.current = l;
            page.current = 1;
            getDeliveries();
          }}
          columns={[
            {
              accessor: 'employee.user.username',
              title: 'Funcionário',
              render: (r) => r.employee?.user?.username || '-',
            },
            {
              accessor: 'employee.sector',
              title: 'Setor',
              render: (r) => r.employee?.sector || '-',
            },
            {
              accessor: 'epi.name',
              title: 'EPI',
              render: (r) => r.epi?.name || '-',
            },
            {
              accessor: 'epi.ca',
              title: 'CA',
              render: (r) => (r.epi?.ca ? `CA ${r.epi.ca}` : '-'),
            },
            {
              accessor: 'quantity',
              title: 'Qtd',
              width: 60,
              render: (r) => r.quantity ?? 1,
            },
            {
              accessor: 'reason',
              title: 'Motivo',
              render: (r) =>
                r.reason ? (
                  <Badge variant="light" color="blue" size="sm">
                    {EpiDeliveryReasonLabel[r.reason] ?? r.reason}
                  </Badge>
                ) : (
                  <Text size="xs" color="dimmed">
                    —
                  </Text>
                ),
            },
            {
              accessor: 'createdAt',
              title: 'Data',
              render: ({ createdAt }) => formatLocale(createdAt, 'DD/MM/YYYY'),
            },
            {
              accessor: 'actions',
              title: '',
              width: 48,
              render: (record) => (
                <ActionIcon
                  color="red"
                  variant="subtle"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(record);
                  }}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              ),
            },
          ]}
        />
      </Flex>
    </Page>
  );
}
